import React, { useState, useEffect } from 'react';
import { MapPin, Star, ChevronLeft, ChevronRight, Phone } from 'lucide-react';
import './Reviews.css';

const Reviews = () => {
  const [currentOfficeIndex, setCurrentOfficeIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const offices = [
    {
      id: 1,
      name: 'Офис в Московском',
      mapId: '65548354645',
      link: 'https://yandex.ru/maps/org/migrant_pro/65548354645/',
      address: 'г. Москва, г. Московский, 1-й мкр., д.39, к.1 (2 этаж)',
      phone: '+7 (925) 246-16-66',
      coords: [37.352147, 55.598462],
      isComingSoon: false
    },
    {
      id: 2, 
      name: 'Офис в Подольске',
      mapId: '120832200151',
      link: 'https://yandex.ru/maps/org/migrant_pro/120832200151/',
      address: 'Московская область, Подольск, пр-д Авиаторов, д. 1 ТЦ "МОЛОТОК", 2 этаж, павильон А-59',
      phone: '+7 (925) 716-66-07',
      coords: [37.557843, 55.459867],
      isComingSoon: false
    },
    {
      id: 3,
      name: 'Офис в Москве',
      mapId: '188162070506',
      link: 'https://yandex.ru/maps/org/migrant_pro/188162070506/',
      address: 'г. Москва, МКАД, 34км, влд. 6',
      phone: '+7 (925) 914-66-67',
      coords: [37.576790, 55.580793],
      isComingSoon: false
    }
  ];

  // Автоматическое переключение
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentOfficeIndex((prev) => (prev + 1) % offices.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, offices.length]);

  const handlePrevious = () => {
    setIsAutoPlaying(false);
    setCurrentOfficeIndex((prev) => (prev - 1 + offices.length) % offices.length);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const handleNext = () => {
    setIsAutoPlaying(false);
    setCurrentOfficeIndex((prev) => (prev + 1) % offices.length);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const handleDotClick = (index) => {
    setIsAutoPlaying(false);
    setCurrentOfficeIndex(index);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  return (
    <>
      <section id="reviews" className="reviews">
        <div className="container">
          <div className="section-header">
            <h2>Отзывы наших клиентов</h2>
            <div className="rating-header">
              <div className="rating-stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={24} fill="#FFD700" color="#FFD700" />
                ))}
              </div>
              <p>Рейтинг 5.0 на Яндекс.Картах</p>
            </div>
          </div>

          <div className="reviews-slider">
            <button 
              className="slider-button slider-button-prev" 
              onClick={handlePrevious}
              aria-label="Предыдущий офис"
            >
              <ChevronLeft size={24} />
            </button>

            <div className="reviews-slider-container">
              <div 
                className="reviews-slider-track"
                style={{ transform: `translateX(-${currentOfficeIndex * 100}%)` }}
              >
                {offices.map((office) => (
                  <div key={office.id} className="review-slide">
                    <div className="review-widget">
                      <h3>
                        {office.isComingSoon ? (
                          <>
                            Отзывы - {office.name}
                            <span className="coming-soon-badge">СКОРО ОТКРЫТИЕ</span>
                          </>
                        ) : (
                          <a href={office.link} target="_blank" rel="noopener noreferrer">
                            Отзывы - {office.name}
                          </a>
                        )}
                      </h3>
                      <p className="office-address">
                        <MapPin size={16} />
                        {office.isComingSoon ? (
                          <span>{office.address}</span>
                        ) : (
                          office.address
                        )}
                      </p>
                      <p className="office-phone">
                        <Phone size={16} />
                        {office.isComingSoon ? (
                          <span className="coming-soon-text">Телефон появится после открытия</span>
                        ) : (
                          <a href={`tel:${office.phone.replace(/\D/g, '')}`}>
                            {office.phone}
                          </a>
                        )}
                      </p>
                      {office.isComingSoon ? (
                        <div className="widget-container coming-soon">
                          <div className="coming-soon-message">
                            <div className="coming-soon-title">Скоро открытие!</div>
                            <div className="coming-soon-subtitle">
                              Новый офис в Москве готовится к открытию.<br/>
                              Следите за обновлениями!
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="widget-container">
                          <iframe 
                            loading="lazy" 
                            src={`https://yandex.ru/maps-reviews-widget/${office.mapId}?comments`}
                            title={`Отзывы ${office.name}`}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button 
              className="slider-button slider-button-next" 
              onClick={handleNext}
              aria-label="Следующий офис"
            >
              <ChevronRight size={24} />
            </button>

            <div className="slider-dots">
              {offices.map((_, index) => (
                <button
                  key={index}
                  className={`slider-dot ${index === currentOfficeIndex ? 'active' : ''}`}
                  onClick={() => handleDotClick(index)}
                  aria-label={`Перейти к офису ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="map" className="map-section">
        <div className="container">
          <div className="section-header">
            <h2>Наши офисы на карте</h2>
            <p>Удобное расположение в Москве и Московской области</p>
          </div>

          <div className="offices-info">
            {offices.map((office, index) => (
              <div 
                key={office.id} 
                className={`office-info-card ${office.isComingSoon ? 'coming-soon' : ''}`}
                onClick={() => {
                  if (!office.isComingSoon) {
                    window.open(office.link, '_blank');
                  }
                }}
                style={{ cursor: office.isComingSoon ? 'default' : 'pointer' }}
              >
                <div className="office-number">{index + 1}</div>
                <div className="office-details">
                  <h4>
                    {office.name}
                    {office.isComingSoon && (
                      <span className="coming-soon-badge">СКОРО</span>
                    )}
                  </h4>
                  <p className="office-address-text">
                    <MapPin size={14} />
                    {office.address}
                  </p>
                  {office.isComingSoon ? (
                    <span className="coming-soon-text">Телефон появится после открытия</span>
                  ) : (
                    <a 
                      href={`tel:${office.phone.replace(/\D/g, '')}`} 
                      className="office-phone-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {office.phone}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="map-container-single">
            <iframe 
              src="https://yandex.ru/map-widget/v1/?ll=37.455000%2C55.540000&z=10&pt=37.352147,55.598462,pm2rdm~37.557843,55.459867,pm2blm~37.576790,55.580793,pm2orm"
              allowFullScreen={true}
              title="Карта офисов Мигрант PRO"
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default Reviews;